import { StrictMode, ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import '@tanstack/devtools';
import '@tanstack/react-query-devtools';

import App from './app/app';
import config from '@/config';
import { hasAppChanged, resetForNewApp } from '@shared/storage';
import { initObservability } from '@shared/observability';
import { loadAppId, startEventLog } from '@shared/events';
import { startReaderStats } from '@entities/reader-stats';
import { installGlobalErrorCapture } from '@shared/lib/debug-log';
import { installViewTransitionTracker } from '@shared/lib/view-transition-tracker';
// From the module that defines the stores, so importing it registers them.
import { applyStoredPreferences } from '@shared/lib/reader-preferences';
import { applyLineHeightScale } from '@shared/lib/hooks';
import {
	getLanguage,
	PSEUDO_LANGUAGE,
	startI18n,
	subscribe,
} from '@shared/i18n';
import { Settings as LuxonSettings } from 'luxon';

import 'animate.css';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';

import './index.css';
import 'virtual:pwa-theme.css';

/**
 * Dev-only, deliberately. `StrictMode` renders no DOM of its own and is
 * effectively inert in a production build, so wrapping unconditionally would
 * also be defensible -- but every one of these ten templates is a live
 * publisher app, and gating on `import.meta.env.DEV` means the tree that
 * ships is byte-for-byte what shipped before.
 *
 * What it buys in dev: React double-invokes component bodies, and mounts →
 * unmounts → remounts every component once, running effect cleanups in
 * between. That surfaces impure renders and missing/incorrect effect teardown
 * at development time instead of as a slow leak on a reader's phone.
 */
const Root = ({ children }: { children: ReactNode }) =>
	import.meta.env.DEV ? <StrictMode>{children}</StrictMode> : children;

const start = () => {
	initObservability();
	installGlobalErrorCapture();
	// Every reader event is kept on the device from here on.
	startEventLog();
	// The reader's own figures, derived from that log on the device.
	startReaderStats();
	// Before the first render, so a first launch's first reader events wait
	// for the app id instead of going out without it. A no-op on every later
	// launch: the id is read from storage.
	if (navigator.onLine) {
		void loadAppId();
	}
	installViewTransitionTracker();
	// The reader's saved line spacing, before the first article renders.
	applyLineHeightScale();
	// And the rest of their Settings that land on <html> (article font and
	// alignment, dark articles, underlined links).
	applyStoredPreferences();
	// Dates follow the app language too (month and day names; the pseudo-locale
	// formats as English).
	const syncDateLocale = () => {
		const language = getLanguage();
		LuxonSettings.defaultLocale =
			language === PSEUDO_LANGUAGE ? 'en' : language;
	};
	syncDateLocale();
	subscribe(syncDateLocale);
	// The reader's language: the default's labels are already in the bundle, so
	// this renders straight away and switches once a saved other language's
	// chunk arrives (preloaded by index.html's pre-paint script, precached by
	// the service worker).
	void startI18n();

	ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
		<Root>
			<App />
		</Root>,
	);
};

// A different app key means this origin last ran another publisher's app:
// nothing it stored belongs here, so wipe it all and start from scratch (the
// reload gives every module the empty state).
if (hasAppChanged(config.build.appKey)) {
	void resetForNewApp(config.build.appKey).finally(() => {
		window.location.reload();
	});
} else {
	start();
}
